import { Component, Input, OnInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AvailabilityRuleDialogComponent } from './availability-rule-dialog.component';
import { ExceptionDialogComponent } from './exception-dialog.component';
import { TimeOffDialogComponent } from './timeoff-dialog.component';
import { BlockDialogComponent } from './block-dialog.component';
import { ConfirmDialogV2, ConfirmDialogDataV2 } from '../../../../../shared/components/confirm-dialog.component';
import { LocationAvailabilityService} from '../../../services/location-availability.service';
import { MasterService } from '../../../../../master/services/master.service';
import { MasterModel } from '../../../../../master/models/master.model';

interface AvailabilityRule {
  availabilityRuleId?: string;
  weekDay: number;
  startTime: string;
  endTime: string;
  /**
   * @deprecated Campo legacy del backend, ya no se usa en la UI. El step de
   * reservas se calcula en el microservicio desde el Concept.
   */
  slotStepMinutes?: number;
  effectiveFrom?: string;
  effectiveTo?: string;
}

/**
 * Empleados internos/reservados que no deben aparecer en los listados
 * del módulo Booking (ni en alta de personal ni en configuración de
 * disponibilidad). Se filtran desde el front por nombre.
 */
const HIDDEN_EMPLOYEE_NAMES: string[] = [
  'Empleados_Por_Año',
  'Empleados_Por_Mes',
  'Empleados_Por_Trimestre'
];

interface AvailabilityException {
  availabilityExceptionId?: string;
  date: string;
  isClosed: boolean;
  startTime?: string;
  endTime?: string;
}

interface TimeOff {
  timeOffId?: string;
  startAt: string;
  endAt: string;
  reason?: string;
}

interface BookingBlock {
  bookingBlockId?: string;
  startAt: string;
  endAt: string;
  reason?: string;
  isActive: boolean;
}

@Component({
    selector: 'app-location-availability-section',
    templateUrl: './location-availability-section.component.html',
    styleUrls: ['./location-availability-section.component.css'],
    standalone: false
})
export class LocationAvailabilitySectionComponent implements OnInit, OnDestroy, OnChanges {
  @Input() locationId!: string;

  staffList: MasterModel[] = [];
  filteredStaffList: MasterModel[] = [];
  staffSearchTerm = '';
  selectedEmployeeUid: string | null = null;
  selectedStaffId: string | null = null; 

  isLoadingEmployees = false;
  isLoadingContext = false;
  isLoadingAction = false;

  errorMessage: string | null = null;
  successMessage: string | null = null;

  private destroy$ = new Subject<void>();

  weekDays = [
    { id: 1, display: 'Lunes' },
    { id: 2, display: 'Martes' },
    { id: 3, display: 'Miércoles' },
    { id: 4, display: 'Jueves' },
    { id: 5, display: 'Viernes' },
    { id: 6, display: 'Sábado' },
    { id: 7, display: 'Domingo' }
  ];

  availabilityRules: AvailabilityRule[] = [];
  exceptions: AvailabilityException[] = [];
  timeOffs: TimeOff[] = [];
  blocks: BookingBlock[] = [];

  constructor(
    private dialog: MatDialog,
    private availabilityService: LocationAvailabilityService,
    private masterService: MasterService
  ) {}

  ngOnInit(): void {
    console.log('LocationAvailabilitySectionComponent initialized with locationId:', this.locationId);
    this.loadEmployees();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.locationId) {
      console.log('locationId changed:', {
        firstChange: changes.locationId.firstChange,
        previousValue: changes.locationId.previousValue,
        currentValue: changes.locationId.currentValue
      });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadEmployees(): void {
    this.isLoadingEmployees = true;
    this.errorMessage = null;

    this.masterService.getEmployees()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (employees: MasterModel[]) => {
          this.staffList = (employees || []).filter(e => !this.isHiddenEmployee(e));
          this.filteredStaffList = [...this.staffList];
          this.isLoadingEmployees = false;
          console.log(`Loaded ${this.staffList.length} employees from master service`);
        },
        error: (err) => {
          console.error('Error loading employees:', err);
          this.errorMessage = 'Error al cargar los empleados. Por favor, inténtalo de nuevo.';
          this.isLoadingEmployees = false;
        }
      });
  }

  /**
   * True si el empleado es uno de los reservados que no deben mostrarse
   * en el módulo Booking.
   */
  private isHiddenEmployee(employee: MasterModel): boolean {
    const name = (employee && employee.display ? employee.display : '').trim().toLowerCase();
    if (!name) {
      return false;
    }
    const hiddenList = HIDDEN_EMPLOYEE_NAMES || [];
    for (let i = 0; i < hiddenList.length; i++) {
      if ((hiddenList[i] || '').trim().toLowerCase() === name) {
        return true;
      }
    }
    return false;
  }

  filterStaff(): void {
    if (!this.staffSearchTerm) {
      this.filteredStaffList = [...this.staffList];
    } else {
      const term = this.staffSearchTerm.toLowerCase();
      this.filteredStaffList = this.staffList.filter(employee =>
        employee.display.toLowerCase().includes(term)
      );
    }
  }

  selectStaff(employeeUid: string): void {
    this.selectedEmployeeUid = employeeUid;
    this.loadStaffAvailabilityContext(employeeUid);
  }

  private loadStaffAvailabilityContext(employeeUid: string): void {
    if (!this.locationId) {
      console.error('No location ID available', {
        locationId: this.locationId
      });
      return;
    }

    this.isLoadingContext = true;
    this.errorMessage = null;

    this.availabilityService.getStaffAvailabilityContext(
      this.locationId,
      employeeUid
    )
    .pipe(takeUntil(this.destroy$))
    .subscribe({
      next: (response) => {
        console.log('getStaffAvailabilityContext response:', response);
        this.isLoadingContext = false;
        
        if (response.isSuccess && response.value) {
          const context = response.value;
          console.log('Context value:', context);
          console.log('merchantStaffId from context:', context.merchantStaffId);
          
          this.selectedStaffId = context.merchantStaffId;
          console.log('selectedStaffId assigned:', this.selectedStaffId);
          
          this.availabilityRules = context.availabilityRules || [];
          this.exceptions = context.availabilityExceptions.map(e => ({
            availabilityExceptionId: e.availabilityExceptionId,
            date: e.exceptionDate,
            isClosed: e.isClosed,
            startTime: e.startTime,
            endTime: e.endTime
          }));
          this.timeOffs = context.timeOffs || [];
          this.blocks = context.bookingBlocks || [];

          console.log(`Loaded availability context for staff ${context.displayName}:`, {
            merchantStaffId: this.selectedStaffId,
            rules: this.availabilityRules.length,
            exceptions: this.exceptions.length,
            timeOffs: this.timeOffs.length,
            blocks: this.blocks.length
          });
        } else if (response.error) {
          this.errorMessage = response.error.messages ? response.error.messages.join(', ') : 'Error al cargar disponibilidad';
        }
      },
      error: (err) => {
        this.isLoadingContext = false;
        console.error('Error loading staff availability context:', err);
        this.errorMessage = 'Error al cargar la disponibilidad del empleado. Por favor, inténtalo de nuevo.';
      }
    });
  }

  getInitials(name: string): string {
    return name
      .split(' ')
      .map(part => part.charAt(0).toUpperCase())
      .slice(0, 2)
      .join('');
  }

  getSelectedStaffName(): string {
    const employee = this.staffList.find(e => e.id === this.selectedEmployeeUid);
    return employee ? employee.display : '';
  }

  getWeekDayName(weekDay: number): string {
    const day = this.weekDays.find(d => d.id === weekDay);
    return day ? day.display : '';
  }

  /**
   * Reglas filtradas por día de la semana, ordenadas por hora de inicio.
   * Usado por las pestañas (Lun-Dom) de "Horario base".
   */
  getRulesForDay(weekDay: number): AvailabilityRule[] {
    return (this.availabilityRules || [])
      .filter(r => r.weekDay === weekDay)
      .sort((a, b) => (a.startTime || '').localeCompare(b.startTime || ''));
  }

  /**
   * Localiza el índice de la regla en el array global `availabilityRules`
   * y delega en editAvailabilityRule / deleteAvailabilityRule, que esperan
   * un índice (comportamiento existente que no queremos cambiar).
   */
  editRule(rule: AvailabilityRule): void {
    const index = this.findRuleIndex(rule);
    if (index !== -1) {
      this.editAvailabilityRule(index);
    }
  }

  deleteRule(rule: AvailabilityRule): void {
    const index = this.findRuleIndex(rule);
    if (index !== -1) {
      this.deleteAvailabilityRule(index);
    }
  }

  private findRuleIndex(rule: AvailabilityRule): number {
    if (!rule) {
      return -1;
    }
    if (rule.availabilityRuleId) {
      return this.availabilityRules.findIndex(r => r.availabilityRuleId === rule.availabilityRuleId);
    }
    return this.availabilityRules.indexOf(rule);
  }

  calculateDuration(startAt: string, endAt: string): string {
    const start = new Date(startAt);
    const end = new Date(endAt);
    const diffMs = end.getTime() - start.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

    if (diffDays > 0) {
      return diffDays + (diffDays === 1 ? ' día' : ' días');
    }
    return diffHours + (diffHours === 1 ? ' hora' : ' horas');
  }

  openAvailabilityRuleDialog(index?: number): void {
    const dialogRef = this.dialog.open(AvailabilityRuleDialogComponent, {
      width: '600px',
      maxWidth: '95vw',
      data: {
        rule: index !== undefined ? this.availabilityRules[index] : null,
        staffName: this.getSelectedStaffName(),
        weekDays: this.weekDays
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      console.log('Dialog closed with result:', result);
      console.log('locationId:', this.locationId);
      console.log('selectedStaffId:', this.selectedStaffId);
      console.log('selectedEmployeeUid:', this.selectedEmployeeUid);
      
      if (result && this.locationId && this.selectedStaffId && this.selectedEmployeeUid) {
        console.log('All conditions met, proceeding with API call');
        this.isLoadingAction = true;
        
        if (index !== undefined) {
          const ruleId = this.availabilityRules[index].availabilityRuleId;
          console.log('Updating rule:', ruleId, 'with data:', result);
          this.availabilityService.updateAvailabilityRule(
            this.locationId,
            this.selectedStaffId,
            ruleId,
            result
          )
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (response) => {
              console.log( 'Update rule response:', response);
              this.isLoadingAction = false;
              if (response.isSuccess) {
                console.log('Rule updated successfully');
                this.availabilityRules[index] = response.value;
                this.showSuccess('Horario actualizado correctamente');
              } else {
                console.error('Rule update failed:', response.error);
                this.showError(this.getErrorMessage(response));
              }
            },
            error: (err) => {
              this.isLoadingAction = false;
              console.error('Error updating availability rule:', err);
              this.showError(this.getErrorMessage(err));
            }
          });
        } else {
          console.log('Creating new rule:', result);
          const hasMultipleWeekDays = Array.isArray(result.weekDays) && result.weekDays.length > 0;

          const createRequest$: any = hasMultipleWeekDays
            ? this.availabilityService.createAvailabilityRulesBulk(
                this.locationId,
                this.selectedStaffId,
                result
              )
            : this.availabilityService.createAvailabilityRule(
                this.locationId,
                this.selectedStaffId,
                result
              );

          createRequest$
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (response) => {
                console.log('Create rule response:', response);
                if (response.isSuccess) {
                  console.log('Rule created successfully, reloading context');
                  this.showSuccess(hasMultipleWeekDays ? 'Horarios creados correctamente' : 'Horario creado correctamente');
                  this.loadStaffAvailabilityContext(this.selectedEmployeeUid!);
                } else {
                  console.error('Rule creation failed:', response.error);
                  this.isLoadingAction = false;
                  this.showError(this.getErrorMessage(response));
                }
              },
              error: (err) => {
                this.isLoadingAction = false;
                console.error('Error creating availability rule:', err);
                this.showError(this.getErrorMessage(err));
              }
            });
        }
      } else {
        console.log('Conditions not met - dialog closed without saving');
        if (!result) {
          console.log('- No result from dialog (user cancelled or form invalid)');
        }
        if (!this.locationId) {
          console.log('- Missing locationId');
        }
        if (!this.selectedStaffId) {
          console.log('- Missing selectedStaffId');
        }
        if (!this.selectedEmployeeUid) {
          console.log('- Missing selectedEmployeeUid');
        }
      }
    });
  }

  editAvailabilityRule(index: number): void {
    this.openAvailabilityRuleDialog(index);
  }

  deleteAvailabilityRule(index: number): void {
    if (!this.locationId || !this.selectedStaffId || !this.selectedEmployeeUid) return;
    
    const dialogRef = this.dialog.open(ConfirmDialogV2, {
      width: '400px',
      maxWidth: '96vw',
      data: {
        title: 'Eliminar horario',
        message: '¿Está seguro de que desea eliminar este horario? Esta acción no se puede deshacer.',
        confirmText: 'Eliminar',
        cancelText: 'Cancelar',
        color: 'warn',
        icon: 'delete'
      } as ConfirmDialogDataV2
    });

    dialogRef.afterClosed().subscribe(confirmed => {
      if (!confirmed) return;
      
      const ruleId = this.availabilityRules[index].availabilityRuleId;
      this.isLoadingAction = true;
      
      this.availabilityService.deleteAvailabilityRule(
        this.locationId,
        this.selectedStaffId,
        ruleId
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.showSuccess('Horario eliminado correctamente');
          this.loadStaffAvailabilityContext(this.selectedEmployeeUid!);
        },
        error: (err) => {
          this.isLoadingAction = false;
          console.error('Error deleting availability rule:', err);
          this.showError(this.getErrorMessage(err));
        },
        complete: () => {
          this.isLoadingAction = false;
        }
      });
    });
  }

  openExceptionDialog(index?: number): void {
    const dialogRef = this.dialog.open(ExceptionDialogComponent, {
      width: '550px',
      maxWidth: '95vw',
      data: {
        exception: index !== undefined ? this.exceptions[index] : null,
        staffName: this.getSelectedStaffName()
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && this.locationId && this.selectedStaffId && this.selectedEmployeeUid) {
        this.isLoadingAction = true;
        
        if (index !== undefined) {
          const exceptionId = this.exceptions[index].availabilityExceptionId;
          this.availabilityService.updateAvailabilityException(
            this.locationId,
            this.selectedStaffId,
            exceptionId,
            result
          )
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (response) => {
              this.isLoadingAction = false;
              if (response.isSuccess) {
                this.exceptions[index] = this.mapExceptionFromDto(response.value);
                this.showSuccess('Excepción actualizada correctamente');
              } else {
                this.showError(this.getErrorMessage(response));
              }
            },
            error: (err) => {
              this.isLoadingAction = false;
              console.error('Error updating exception:', err);
              this.showError(this.getErrorMessage(err));
            }
          });
        } else {
          this.availabilityService.createAvailabilityException(
            this.locationId,
            this.selectedStaffId,
            result
          )
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (response) => {
              if (response.isSuccess) {
                this.showSuccess('Excepción creada correctamente');
                this.loadStaffAvailabilityContext(this.selectedEmployeeUid!);
              } else {
                this.isLoadingAction = false;
                this.showError(this.getErrorMessage(response));
              }
            },
            error: (err) => {
              this.isLoadingAction = false;
              console.error('Error creating exception:', err);
              this.showError(this.getErrorMessage(err));
            }
          });
        }
      }
    });
  }

  editException(index: number): void {
    this.openExceptionDialog(index);
  }

  deleteException(index: number): void {
    if (!this.locationId || !this.selectedStaffId || !this.selectedEmployeeUid) return;
    
    const dialogRef = this.dialog.open(ConfirmDialogV2, {
      width: '400px',
      maxWidth: '96vw',
      data: {
        title: 'Eliminar excepción',
        message: '¿Está seguro de que desea eliminar esta excepción de disponibilidad? Esta acción no se puede deshacer.',
        confirmText: 'Eliminar',
        cancelText: 'Cancelar',
        color: 'warn',
        icon: 'delete'
      } as ConfirmDialogDataV2
    });

    dialogRef.afterClosed().subscribe(confirmed => {
      if (!confirmed) return;
      
      const exceptionId = this.exceptions[index].availabilityExceptionId;
      this.isLoadingAction = true;
      
      this.availabilityService.deleteAvailabilityException(
        this.locationId,
        this.selectedStaffId,
        exceptionId
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.showSuccess('Excepción eliminada correctamente');
          this.loadStaffAvailabilityContext(this.selectedEmployeeUid!);
        },
        error: (err) => {
          this.isLoadingAction = false;
          console.error('Error deleting exception:', err);
          this.showError(this.getErrorMessage(err));
        },
        complete: () => {
          this.isLoadingAction = false;
        }
      });
    });
  }

  openTimeOffDialog(index?: number): void {
    const dialogRef = this.dialog.open(TimeOffDialogComponent, {
      width: '600px',
      maxWidth: '95vw',
      data: {
        timeOff: index !== undefined ? this.timeOffs[index] : null,
        staffName: this.getSelectedStaffName()
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && this.locationId && this.selectedStaffId && this.selectedEmployeeUid) {
        this.isLoadingAction = true;
        
        if (index !== undefined) {
          const timeOffId = this.timeOffs[index].timeOffId;
          this.availabilityService.updateTimeOff(
            this.locationId,
            this.selectedStaffId,
            timeOffId,
            result
          )
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (response) => {
              this.isLoadingAction = false;
              if (response.isSuccess) {
                this.timeOffs[index] = response.value;
                this.showSuccess('Ausencia actualizada correctamente');
              } else {
                this.showError(this.getErrorMessage(response));
              }
            },
            error: (err) => {
              this.isLoadingAction = false;
              console.error('Error updating time off:', err);
              this.showError(this.getErrorMessage(err));
            }
          });
        } else {
          this.availabilityService.createTimeOff(
            this.locationId,
            this.selectedStaffId,
            result
          )
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (response) => {
              if (response.isSuccess) {
                this.showSuccess('Ausencia creada correctamente');
                this.loadStaffAvailabilityContext(this.selectedEmployeeUid!);
              } else {
                this.isLoadingAction = false;
                this.showError(this.getErrorMessage(response));
              }
            },
            error: (err) => {
              this.isLoadingAction = false;
              console.error('Error creating time off:', err);
              this.showError(this.getErrorMessage(err));
            }
          });
        }
      }
    });
  }

  editTimeOff(index: number): void {
    this.openTimeOffDialog(index);
  }

  deleteTimeOff(index: number): void {
    if (!this.locationId || !this.selectedStaffId || !this.selectedEmployeeUid) return;
    
    const dialogRef = this.dialog.open(ConfirmDialogV2, {
      width: '400px',
      maxWidth: '96vw',
      data: {
        title: 'Eliminar ausencia',
        message: '¿Está seguro de que desea eliminar esta ausencia? Esta acción no se puede deshacer.',
        confirmText: 'Eliminar',
        cancelText: 'Cancelar',
        color: 'warn',
        icon: 'delete'
      } as ConfirmDialogDataV2
    });

    dialogRef.afterClosed().subscribe(confirmed => {
      if (!confirmed) return;
      
      const timeOffId = this.timeOffs[index].timeOffId;
      this.isLoadingAction = true;
      
      this.availabilityService.deleteTimeOff(
        this.locationId,
        this.selectedStaffId,
        timeOffId
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.showSuccess('Ausencia eliminada correctamente');
          this.loadStaffAvailabilityContext(this.selectedEmployeeUid!);
        },
        error: (err) => {
          this.isLoadingAction = false;
          console.error('Error deleting time off:', err);
          this.showError(this.getErrorMessage(err));
        },
        complete: () => {
          this.isLoadingAction = false;
        }
      });
    });
  }

  openBlockDialog(index?: number): void {
    const dialogRef = this.dialog.open(BlockDialogComponent, {
      width: '600px',
      maxWidth: '95vw',
      data: {
        block: index !== undefined ? this.blocks[index] : null,
        staffName: this.getSelectedStaffName()
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && this.locationId && this.selectedStaffId && this.selectedEmployeeUid) {
        this.isLoadingAction = true;
        
        if (index !== undefined) {
          const blockId = this.blocks[index].bookingBlockId;
          this.availabilityService.updateBookingBlock(
            this.locationId,
            this.selectedStaffId,
            blockId,
            result
          )
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (response) => {
              this.isLoadingAction = false;
              if (response.isSuccess) {
                this.blocks[index] = response.value;
                this.showSuccess('Bloqueo actualizado correctamente');
              } else {
                this.showError(this.getErrorMessage(response));
              }
            },
            error: (err) => {
              this.isLoadingAction = false;
              console.error('Error updating booking block:', err);
              this.showError(this.getErrorMessage(err));
            }
          });
        } else {
          this.availabilityService.createBookingBlock(
            this.locationId,
            this.selectedStaffId,
            result
          )
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (response) => {
              if (response.isSuccess) {
                this.showSuccess('Bloqueo creado correctamente');
                this.loadStaffAvailabilityContext(this.selectedEmployeeUid!);
              } else {
                this.isLoadingAction = false;
                this.showError(this.getErrorMessage(response));
              }
            },
            error: (err) => {
              this.isLoadingAction = false;
              console.error('Error creating booking block:', err);
              this.showError(this.getErrorMessage(err));
            }
          });
        }
      }
    });
  }

  editBlock(index: number): void {
    this.openBlockDialog(index);
  }

  deleteBlock(index: number): void {
    if (!this.locationId || !this.selectedStaffId || !this.selectedEmployeeUid) return;
    
    const dialogRef = this.dialog.open(ConfirmDialogV2, {
      width: '400px',
      maxWidth: '96vw',
      data: {
        title: 'Eliminar bloqueo',
        message: '¿Está seguro de que desea eliminar este bloqueo de citas? Esta acción no se puede deshacer.',
        confirmText: 'Eliminar',
        cancelText: 'Cancelar',
        color: 'warn',
        icon: 'delete'
      } as ConfirmDialogDataV2
    });

    dialogRef.afterClosed().subscribe(confirmed => {
      if (!confirmed) return;
      
      const blockId = this.blocks[index].bookingBlockId;
      this.isLoadingAction = true;
      
      this.availabilityService.deleteBookingBlock(
        this.locationId,
        this.selectedStaffId,
        blockId
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.showSuccess('Bloqueo eliminado correctamente');
          this.loadStaffAvailabilityContext(this.selectedEmployeeUid!);
        },
        error: (err) => {
          this.isLoadingAction = false;
          console.error('Error deleting booking block:', err);
          this.showError(this.getErrorMessage(err));
        },
        complete: () => {
          this.isLoadingAction = false;
        }
      });
    });
  }

  toggleBlockActive(index: number): void {
    if (!this.locationId || !this.selectedStaffId) return;
    
    const block = this.blocks[index];
    block.isActive = !block.isActive;
    
    this.isLoadingAction = true;
    this.availabilityService.updateBookingBlock(
      this.locationId,
      this.selectedStaffId,
      block.bookingBlockId,
      block
    )
    .pipe(takeUntil(this.destroy$))
    .subscribe({
      next: (response) => {
        this.isLoadingAction = false;
        if (response.isSuccess) {
          this.blocks[index] = response.value;
          const status = block.isActive ? 'activado' : 'desactivado';
          this.showSuccess(`Bloqueo ${status} correctamente`);
        } else {
          block.isActive = !block.isActive;
          this.showError(this.getErrorMessage(response));
        }
      },
      error: (err) => {
        this.isLoadingAction = false;
        console.error('Error toggling block active:', err);
        block.isActive = !block.isActive;
        this.showError(this.getErrorMessage(err));
      }
    });
  }

  private mapExceptionFromDto(dto: any): AvailabilityException {
    return {
      availabilityExceptionId: dto.availabilityExceptionId,
      date: dto.exceptionDate,
      isClosed: dto.isClosed,
      startTime: dto.startTime,
      endTime: dto.endTime
    };
  }

  private getErrorMessage(err: any): string {
    if (!err) return 'Ha ocurrido un error inesperado. Por favor, intenta nuevamente.';
    
    if (err.error && typeof err.error === 'string') {
      if (err.error.includes('Response status code does not indicate success')) {
        return this.getFriendlyErrorByStatus(err.status);
      }
      
      try {
        const match = err.error.match(/Error: (\{.*\})/);
        if (match && match[1]) {
          const errorObj = JSON.parse(match[1]);
          if (errorObj.detail) {
            return errorObj.detail;
          }
          if (errorObj.title && errorObj.title !== 'Validation Error') {
            return errorObj.title;
          }
        }
      } catch (e) {
        const cleanError = err.error.trim();
        if (cleanError && !cleanError.includes('Response status code') && cleanError.length < 200) {
          return cleanError;
        }
      }
    }
    
    if (err.error && err.error.detail) {
      return err.error.detail;
    }
    if (err.error && err.error.messages && err.error.messages.length > 0) {
      return err.error.messages.join(', ');
    }
    if (err.error && err.error.title && err.error.title !== 'Validation Error') {
      return err.error.title;
    }
    
    if (err.message) {
      if (err.message.includes('Response status code does not indicate success')) {
        return this.getFriendlyErrorByStatus(err.status);
      }
      if (!err.message.includes('Http failure') && err.message.length < 200) {
        return err.message;
      }
    }
    
    return this.getFriendlyErrorByStatus(err.status);
  }

  private getFriendlyErrorByStatus(status: number): string {
    switch (status) {
      case 400:
        return 'Los datos enviados no son válidos. Por favor, verifica la información e intenta nuevamente.';
      case 401:
        return 'No tienes autorización para realizar esta acción.';
      case 403:
        return 'No tienes permisos para realizar esta acción.';
      case 404:
        return 'El recurso solicitado no se encuentra disponible.';
      case 409:
        return 'La información ya existe o hay un conflicto con los datos actuales.';
      case 422:
        return 'Los datos enviados no cumplen con las validaciones requeridas.';
      case 500:
        return 'Ha ocurrido un error en el servidor. Por favor, intenta nuevamente más tarde.';
      case 503:
        return 'El concepto no está disponible temporalmente. Por favor, intenta nuevamente más tarde.';
      default:
        return 'Ha ocurrido un error inesperado. Por favor, intenta nuevamente.';
    }
  }

  private showError(message: string): void {
    this.successMessage = null;
    this.errorMessage = message;

    setTimeout(() => {
      this.errorMessage = null;
    }, 8000);
  }

  private showSuccess(message: string): void {
    this.errorMessage = null;
    this.successMessage = message;

    setTimeout(() => {
      this.successMessage = null;
    }, 5000);
  }
  public trackByIndex(i: number) { return i; }

}
